import { browserHistory } from '../helpers/components';
import _get from 'lodash/get';

import { MEMBER_STATUS_APPLIED } from '../constants/backend';

import { safe, gtmPush, sessionStorageGetJson, getPrevAction } from '../utils/utils';

import { STORAGE_KEY } from '../constants/generic';
import {
  getIncompleteUserStatus,
  getActionError,
  getAppliedUserStatus
} from '../helpers/selectors';
import { SERVER_ERROR } from '../helpers/errors';
import { addActionError } from '../utils/flashMessage/creator';
import { normalizePublicCompaniesResponse } from '../helpers/ssoRegistrationFlow';

import {
  clearAuthTokens,
  clearSsoTokens,
  parseAuthTokensFromApi,
  saveAuthTokens,
  saveSsoTokens
} from '../utils/tokens';

function parseAuthTokens(action) {
  if (action.type === 'keycloak/VERIFY_USER_SUCCESS') {
    saveSsoTokens();
  } else {
    const tokens = parseAuthTokensFromApi(action?.payload?.headers);
    const meTokens = parseAuthTokensFromApi(action?.payload?.config?.headers);

    saveAuthTokens(tokens.token ? tokens : meTokens);
  }
}

function clearDataOnLogout(state) {
  sessionStorage.removeItem(STORAGE_KEY.USER);
  clearAuthTokens();
  clearSsoTokens();

  return {
    ...state,
    data: null,
    isRegisterIncompleteUser: false,
    profiles: null
  };
}

function getRegisterIncompleteUserStatus(userData) {
  return getIncompleteUserStatus(userData) || getAppliedUserStatus(userData);
}

// A member the backend has just answered with, taken into the slice together with the
// "his registration is still unfinished" reading of him, so nothing downstream can
// catch one without the other. This is for the answers that BRING a member the flow
// did not have: /me finding one (keycloak/VERIFY_USER_SUCCESS) and the registration
// making one (user/REGISTER_SSO_SUCCESS).
//
// A refresh of the member already in the slice deliberately does NOT come through here
// - user/GET_INFO_SUCCESS and user/UPDATE_INFO_SUCCESS assign `data` on their own, as
// does the verifyUser(false) PersonalInfo fires after registering. Every step of
// Creation/ saves through UPDATE_INFO, and re-reading the flag there would move it
// mid-registration: the heading over the flow and its step counter are chosen from it,
// and they may not change under the visitor between two steps. It is refreshed on the
// boundaries of the flow instead - user/AUTO_UPDATE_REGISTER_INCOMPLETE_STATUS in the
// Creation cleanup.
function storeMember(state, user) {
  return {
    ...state,
    data: user,
    isRegisterIncompleteUser: getRegisterIncompleteUserStatus(user)
  };
}

// The member each of the two registration answers hands back. They end the same submit
// and report the same GTM success; this one thing is all that parts them, so it is
// stated in a row per action rather than read off the action type again inside the
// branch they share - and an action added to that branch later gets no member by
// default rather than someone else's.
//
// The registration answers with the member it has just created, in the shape /me
// returns, and until now that answer was read for its type and thrown away. Keeping him
// is what gives the flow a member at the step that made him, rather than one round trip
// later when the /me PersonalInfo fires comes back - a call that can fail, or be
// abandoned by a Back press, leaving the visitor mid-flow as nobody at all. The profile
// link answers about a profile, and the member behind it is already in the slice.
const ANSWERED_MEMBER_BY_ACTION = {
  'user/REGISTER_SSO_SUCCESS': (action) => action.payload?.data,
  'user/LINK_PROFILE_SUCCESS': () => null
};

const fetchingProps = {
  sendEmailFetching: false,
  updateSettingsFetching: false,
  inviteCodeFetching: false,
  userDataUpdating: false,
  companyIdFetching: false,
  showSubscribeLoader: false
};

const creationSuccessDataDefaults = Object.freeze({
  email: '',
  firstName: ''
});

const getDefaultState = () => ({
  updateError: null,
  companyIdError: null,
  data: sessionStorageGetJson(STORAGE_KEY.USER),
  email: null,
  activationSuccess: false,
  alreadyActivated: false,
  activationExpired: false,
  sendEmailSuccess: false,
  inviteError: null,
  company: false,
  companyBrand: false,
  registerCompany: null,
  brandCompanies: null,
  registerInnerStateData: null,
  creationSuccessData: creationSuccessDataDefaults,
  settings: null,
  profiles: null,
  isRegisterIncompleteUser: false,
  ...fetchingProps
});

export default function reducer(state = getDefaultState(), action) {
  switch (action.type) {
    case 'user/SAVE_REGISTER_INNER_STATE_DATA': {
      state = { ...state, registerInnerStateData: action.data };
      break;
    }

    case 'user/CLEAR_REGISTER_INNER_STATE_DATA': {
      state = { ...state, registerInnerStateData: null };
      break;
    }

    case 'user/SAVE_CREATION_SUCCESS_DATA': {
      state = { ...state, creationSuccessData: { ...action.data } };
      break;
    }

    case 'user/CLEAR_CREATION_SUCCESS_DATA': {
      state = { ...state, creationSuccessData: creationSuccessDataDefaults };
      break;
    }

    case 'user/SET_REGISTER_INCOMPLETE_STATUS': {
      state = { ...state, isRegisterIncompleteUser: action.status };
      break;
    }

    case 'user/AUTO_UPDATE_REGISTER_INCOMPLETE_STATUS': {
      state = {
        ...state,
        isRegisterIncompleteUser: getRegisterIncompleteUserStatus(state.data)
      };
      break;
    }

    case 'user/SILENT_REFRESH_SUCCESS': {
      saveAuthTokens(parseAuthTokensFromApi(action.payload.headers));
      break;
    }

    case 'user/REFRESH_SUCCESS':
    case 'keycloak/VERIFY_USER_SUCCESS': {
      const user = action?.payload?.data;
      const { updateIncompleteStatus = true } = getPrevAction(action);

      parseAuthTokens(action);

      state = updateIncompleteStatus
        ? storeMember(state, user)
        : { ...state, data: user };

      gtmPush(
        { event: 'Authentification', authentificationType: 'success' },
        _get(state, 'data.id')
      );

      break;
    }

    case 'user/GET_SETTINGS':
    case 'user/UPDATE_SETTINGS': {
      state = { ...state, updateSettingsFetching: true };
      break;
    }

    case 'user/UPDATE_SETTINGS_SUCCESS':
    case 'user/GET_SETTINGS_SUCCESS': {
      const settings = action.payload.data;

      state = { ...state, settings, updateSettingsFetching: false };
      break;
    }

    case 'user/UPDATE_SETTINGS_FAIL':
    case 'user/GET_SETTINGS_FAIL': {
      state = { ...state, updateSettingsFetching: false };
      break;
    }

    case 'user/LOGOUT':
    case 'user/SELF_ANONYMIZE_SUCCESS': {
      state = clearDataOnLogout(state);
      if (action.redirect) browserHistory.push('/');
      break;
    }

    case 'user/LINK_PROFILE':
    case 'user/REGISTER_SSO': {
      state = { ...state, userDataUpdating: true };
      break;
    }

    case 'user/LINK_PROFILE_SUCCESS':
    case 'user/REGISTER_SSO_SUCCESS': {
      const answeredMember = ANSWERED_MEMBER_BY_ACTION[action.type]?.(action);

      state = { ...state, userDataUpdating: false };

      if (answeredMember) state = storeMember(state, answeredMember);

      gtmPush({
        event: 'Registration',
        registrationType: 'success'
      });

      break;
    }

    case 'user/LINK_PROFILE_FAIL':
    case 'user/REGISTER_SSO_FAIL': {
      state = { ...state, userDataUpdating: false };
      break;
    }

    case 'user/SET_SUBSCRIBE_LOADER_STATE': {
      state = { ...state, showSubscribeLoader: action.payload };
      break;
    }

    case 'user/FINAL_ACCESS_DENIED': {
      state = clearDataOnLogout(state);
      break;
    }

    case 'user/SEND_ACTIVATION_EMAIL': {
      state = { ...state, sendEmailFetching: true, sendEmailSuccess: false };
      break;
    }

    case 'user/SEND_ACTIVATION_EMAIL_SUCCESS': {
      state = { ...state, sendEmailSuccess: true, sendEmailFetching: false };
      break;
    }

    case 'user/SEND_ACTIVATION_EMAIL_FAIL': {
      if (typeof _get(action, 'error.response.data.developerMessage') === 'string') {
        const devMsgArray = getActionError(action).developerMessage.split(' ');
        const userActivated = _get(devMsgArray, '[3]') === 'already';
        const userNotFound = _get(devMsgArray, '[2]') === 'found';

        if (userNotFound || userActivated) state = { ...state, sendEmailSuccess: true };
      }

      state = { ...state, sendEmailFetching: false };
      break;
    }

    case 'user/ACCOUNT_ACTIVATION': {
      state = { ...state, alreadyActivated: false, activationExpired: false };
      break;
    }

    case 'user/ACCOUNT_ACTIVATION_SUCCESS': {
      state = {
        ...state,
        activationSuccess: true,
        alreadyActivated: false,
        activationExpired: false
      };
      break;
    }

    case 'user/ACCOUNT_ACTIVATION_FAIL': {
      if (_get(action, 'error.response.status') === 410) {
        state = { ...state, activationExpired: true };
      } else if (
        typeof _get(action, 'error.response.data.developerMessage') === 'string'
      ) {
        const devMsgArray = getActionError(action).developerMessage.split(' ');
        const userActivated = _get(devMsgArray, '[5]') === 'status';
        const status = _get(devMsgArray, '[6]');

        if (userActivated && status !== MEMBER_STATUS_APPLIED)
          state = { ...state, alreadyActivated: true };
      } else {
        addActionError(action);
      }

      break;
    }

    case 'user/REMOVE_UPDATE_ERRORS': {
      state = { ...state, updateError: null };
      break;
    }

    case 'user/GET_INFO_SUCCESS': {
      let user = { ...action.payload.data };

      state = { ...state, data: user };
      break;
    }

    case 'user/GET_INFO_FAIL': {
      const { silent } = getPrevAction(action);

      if (!silent) addActionError(action);
      break;
    }

    case 'user/UPDATE_INFO': {
      state = { ...state, userDataUpdating: true };
      break;
    }

    case 'user/UPDATE_INFO_SUCCESS': {
      const user = action.payload.data;

      state = { ...state, data: user, userDataUpdating: false };
      break;
    }

    case 'user/UPDATE_INFO_FAIL': {
      state = {
        ...state,
        userDataUpdating: false
      };


      break;
    }

    case 'user/GET_FILE_INFO_SUCCESS': {
      break;
    }

    case 'user/GET_COMPANY_ID': {
      state = { ...state, companyIdFetching: true, companyIdError: null };
      break;
    }

    case 'user/GET_COMPANY_ID_FAIL': {
      state = { ...state, companyIdFetching: false, companyIdError: true };
      break;
    }

    case 'user/GET_COMPANY_ID_SUCCESS': {
      state = { ...state, companyIdFetching: false };
      break;
    }

    case 'user/GET_COMPANY_SUCCESS': {
      state = { ...state, company: action.payload.data };
      break;
    }

    case 'user/CHECK_INVITE_CODE': {
      state = { ...state, inviteCodeFetching: true, inviteError: null };
      break;
    }

    case 'user/CHECK_INVITE_CODE_FAIL': {
      // there is no body to read on a network failure or an empty 5xx: report the
      // transport problem instead of throwing and leaving the form spinning
      const inviteError = safe(() => action.error.response.data) || SERVER_ERROR;

      state = {
        ...state,
        inviteError,
        inviteCodeFetching: false
      };

      break;
    }

    case 'user/CHECK_INVITE_CODE_ERROR': {
      state = {
        ...state,
        inviteError: action.error,
        inviteCodeFetching: false
      };
      break;
    }

    case 'user/CHECK_INVITE_CODE_SUCCESS': {
      const { keepFetching } = getPrevAction(action);

      state = { ...state, inviteCodeFetching: keepFetching };
      break;
    }

    case 'user/CHECK_INVITE_CODE_FETCH_DONE': {
      state = { ...state, inviteCodeFetching: false };
      break;
    }

    case 'user/CHECK_INVITE_CODE_CLEAN_ERROR': {
      state = {
        ...state,
        inviteError: null
      };
      break;
    }

    case 'user/GET_REGISTER_COMPANY_SUCCESS': {
      state = { ...state, registerCompany: action.payload.data };
      break;
    }

    case 'user/GET_BRAND_COMPANIES': {
      state = { ...state, brandCompanies: null };
      break;
    }

    case 'user/GET_BRAND_COMPANIES_SUCCESS': {
      // The endpoint answers with a paginated {results} envelope, which the old
      // `data.length` read never saw - the list silently stayed null. A successful
      // empty response stores [] so "no companies" stays tellable from "not loaded"
      // (null); an unrecognised shape stays null for the same reason.
      state = {
        ...state,
        brandCompanies: normalizePublicCompaniesResponse(action.payload.data)
      };
      break;
    }

    case 'user/GET_BRAND_COMPANIES_FAIL': {
      // Surface the error; brandCompanies stays null ("not loaded") - a failed
      // request is never presented as a brand with zero companies.
      addActionError(action);
      break;
    }

    case 'user/GET_REGISTER_COMPANY_FAIL': {
      addActionError(action);
      break;
    }

    case 'user/GET_COMPANY_FOR_BRAND_SUCCESS': {
      state = { ...state, companyBrand: action.payload.data };
      break;
    }

    case 'user/CLEAR_REGISTER_COMPANY_DATA': {
      state = { ...state, registerCompany: null };
      break;
    }

    case 'user/GET_PROFILES_SUCCESS': {
      state = { ...state, profiles: action.payload.data };
      break;
    }

    case 'user/GET_PROFILES_FAIL': {
      addActionError(action);
      state = { ...state, profiles: null };
      break;
    }

    default: {
      break;
    }
  }

  return state;
}
